import { 
         CircularProgress, 
         DialogContent, 
         FormControl, 
         FormHelperText, 
         IconButton, 
         InputAdornment, 
         MenuItem, 
         OutlinedInput, 
         Select, 
         SelectChangeEvent, 
         styled, 
         TextField, 
         Typography, 
         useTheme
       }                        from "@mui/material"
import styles                   from './EventEditDialog.module.scss'
import { ScheduledEvent, 
         UnionKeyToValue 
       }                        from "types"
import { useEffect, 
         useState 
       }                        from "react"
import { DateTimePicker, 
         LocalizationProvider 
       }                        from "@mui/x-date-pickers"
import { AdapterDateFns }       from "@mui/x-date-pickers/AdapterDateFns"
import { EventManagementAPI }   from "apis/eventManagementAPI"
import { useGlobalConfig }      from "context/GlobalContext"
import CheckCircleOutlineIcon   from '@mui/icons-material/CheckCircleOutline'
import { EVENTS }               from "framework/events/EventSystem"
import { 
         BglDialog, 
         BglDialogActions, 
         BglDialogTitle 
       }                        from "components/dialog/BglDialogStyles"
import RefreshIcon              from '@mui/icons-material/Refresh'
import { EVENT_MANAGEMENT_STRINGS } from "types/translations/EventManagementStrings"
import { BglCloseButton, BglPrimaryButton }           from "components/button/BglButtons"
import moment  from 'moment-timezone';
import { AppFlavour } from "configs/AppFlavours"
import { formatOngageUserCountData, getGSPACE_WEBHOOKURL, triggerGoogleSpacesAPI } from "helpers/helper"
import { useRequestApiData } from "hooks/useRequestApiData"
import { GetUserLoadAPI } from "types/userManagement/userManagementInterface"
import { UserManagementAPI } from "apis/userManagementAPI"

export type MODE = 'ADD' | 'EDIT'

export const MODE : UnionKeyToValue<MODE> = {
  ADD   : 'ADD',
  EDIT  : 'EDIT'
}

const BglDateTimePicker = styled(DateTimePicker)(({theme}) => ({

  '.MuiInputBase-root': {
    background: 'white'
  },

  '.MuiIconButton-root': {
    color: theme.palette.primary.main
  },
}))

const BglSelect = styled<any>(Select)(({theme}) => ({

  '.MuiSelect-select' : {
    background : 'white',
    fontSize : '14px'
  }
}))

export interface ScheduledEventDetails {
  eventType       : ScheduledEvent.EventType
  eventName       : string
  startTimeEpoch  : number
  endTimeEpoch    : number
}

export interface ScheduledEventDialogProps {
  eventType ?: ScheduledEvent.EventType
  startDate ?: Date
  endDate ?: Date
  scheduledEvent ?: ScheduledEvent
  mode : MODE
  open : boolean
  handleClose : (event : any, reason ?: string) => void
}

export const EventEditDialog = (props : ScheduledEventDialogProps) => {

  const { rc } = useGlobalConfig()
  const { translations , config } = rc;
  const supportedEvents = JSON.parse(config.getConfig('EVENT_MANAGEMENT.SUPPORTED_CAMPAIGN_TYPES'));
  const pilotTimeZone = config.getConfig('EVENT_MANAGEMENT.PILOT_TIMEZONE') || Intl.DateTimeFormat().resolvedOptions().timeZone;
  //Get time difference between pilot and local timezone.
  const timeDiff = rc.dateUtils.getTimeDifference( Intl.DateTimeFormat().resolvedOptions().timeZone,pilotTimeZone) as number;

  const [eventType, setEventType] = useState(
      props.scheduledEvent ? props.scheduledEvent.eventType : props.eventType ? props.eventType : '')

  const [eventName, setEventName] = useState(
      props.scheduledEvent ? props.scheduledEvent.eventName : '')
  
  const [startDate, setStartDate] = useState(
      props.scheduledEvent ? rc.dateUtils.addMinutesToDate(new Date(props.scheduledEvent.startTimeEpoch * 1000),timeDiff) 
      : props.startDate ? rc.dateUtils.addMinutesToDate(props.startDate,timeDiff) : null)
  
  const [endDate, setEndDate] = useState(
      props.scheduledEvent ? rc.dateUtils.addMinutesToDate(new Date(props.scheduledEvent.endTimeEpoch * 1000),timeDiff) 
      : props.endDate ? rc.dateUtils.addMinutesToDate(props.endDate,timeDiff) : null)

  const [endDateError, setEndDateError] = useState('')

  const [duration, setDuration] = useState('0m')
  const [ durDisclaimer, setDurDisclaimer ] = useState('')

  const theme = useTheme();

  const [{ loading, data, error }, 
      setApiState] = useState<any>({ loading : false, data : null, error : null })
  
  const handleEventTypeChange = (event : SelectChangeEvent<ScheduledEvent.EventType>) => {
    const value = event.target.value as ScheduledEvent.EventType
    setEventType(value)
  }

  const setEventNameFromDates = (startDate : Date, endDate : Date, refresh : boolean = false) => {

    if (!eventType || !startDate || !endDate || 
        !rc.dateUtils.isValidDate(startDate) || 
        !rc.dateUtils.isValidDate(endDate)) return

    const name = eventType + '_' + 
      rc.dateUtils.formatDate(startDate, 'ddMMMyyHHmm') + '_' +
      rc.dateUtils.formatDate(endDate, 'ddMMMyyHHmm')
    if (!eventName || refresh) setEventName(name)

    //Log mixpanel event if refresh name clicked
    if(refresh){
      rc.analytics.trackEvent('REFRESH_NAME_BTN_CLICKED', {
        startDate,
        endDate,
        eventName,
        eventType,
        newName: name
      })
    }

  }

  useEffect(() => {
    if (endDate && startDate) {
      const dur = rc.dateUtils.getDurationDifference(startDate, endDate)
      if (dur !== -1) {
        setDuration(dur as string)
        if (!eventName) setEventNameFromDates(startDate, endDate)
      } 
    }
  }, [])

  const setDurationForDates = (startDate ?: Date, endDate ?: Date) => {

    setDurDisclaimer('')

    if (!startDate || !endDate) {
      setDuration('')
      return
    }

    const durMilli = (endDate.getTime() - startDate.getTime())
    
    if (endDate.getTime() - startDate.getTime() > 6 * 60 * 60 * 1000) {
      setDurDisclaimer('The event is set for more than 6 hours. Please double check before scheduling the event.')
    }

    setDuration(rc.dateUtils.getDurationObject(durMilli).durStr)
  }

  const onChangeStartDate = (date : any, keyboardInputValue ?: string) => {
    
    try {
      if (date === null) setDurationForDates()
      setStartDate(date!)
      if (endDate) {
        setDurationForDates(date, endDate)
        setEventNameFromDates(date, endDate)
      }
    } catch (error) {}
  }

  const onChangeEndDate = (date : any, keyboardInputValue ?: string) => {
    
    try {
      if (date === null) setDurationForDates()
      setEndDate(date!)
      if (startDate) {
        setDurationForDates(startDate, date)
        setEventNameFromDates(startDate, date)
      }
    } catch (error) {}
  }

  const handleSubmit = async (event : any) => {

    event.preventDefault()

    if (loading) return
    
    if (endDate! <= startDate!) {
      setEndDateError(translations.getTranslation(EVENT_MANAGEMENT_STRINGS.VALIDATIONS.INVALID_DATE_TIME))
      return
    } else {
      setEndDateError('')
    }

    setApiState({ loading : true, error : null, data : null })
    
    let resp
    
    //Add time difference to start and end date to get corressponding timezone Date.
    const updatedStart = rc.dateUtils.addMinutesToDate(startDate!, -1 * timeDiff);
    const updatedEnd = rc.dateUtils.addMinutesToDate(endDate!, -1 * timeDiff);

    let params = {
      pilotId : rc.session.getPilotId(),
      eventType : eventType as ScheduledEvent.EventType, 
      eventName : eventName, 
      startTimeEpoch : updatedStart!.getTime() / 1000,
      endTimeEpoch : updatedEnd!.getTime()/ 1000,
      timezoneOffsetMins : rc.dateUtils.getTimezoneOffset()
    }
    if (props.mode === MODE.ADD) {
      rc.analytics.trackEvent('SCHEDULE_EVENT_CONFIRM_CLICKED', params);
      resp = await EventManagementAPI.createEvent(rc, params)
    } else {
      const editParams = { ...params,
        eventId : props.scheduledEvent!.eventId
      }
      rc.analytics.trackEvent('EDIT_EVENT_CONFIRM_CLICKED', editParams);
      resp = await EventManagementAPI.editEvent(rc, editParams)
    }

    // Trigger Google Spaces API only for CPP
    if (resp) {
      const data : any = await UserManagementAPI.fetchUserLoad(rc, { pilotId: rc.session.getPilotId(), campaignType : params.eventType });
      let message = `Regular Event is scheduled by Pilot *${rc.session.getPilotId()}*. 
      Start Time: *${new Date(params.startTimeEpoch * 1000)}*
      End Time: *${new Date(params.endTimeEpoch * 1000)}*
      Status: *${resp.error ? "Failed" : "Success"}*
      Response: *${JSON.stringify(resp)}*
      Environment: *${AppFlavour.getEnvironmentFromURL(window.location.host)}*
      `;
      
      if(data?.data){
        const ongageData = formatOngageUserCountData(data?.data);
        message += `Active Users: *${ongageData.activeUsers}*
        Unsubscribed Users: *${ongageData.unsubscribed}*`
      }

      const webhookUrl = getGSPACE_WEBHOOKURL(params.eventType);
      triggerGoogleSpacesAPI(message, webhookUrl);
    }

    if (resp.error) {
      setApiState({ loading : false, error : resp.error, data : null });
      if(props.mode === MODE.ADD){
        rc.analytics.trackEvent('SCHEDULE_EVENT_FAILED', params);
      } else {
        rc.analytics.trackEvent('EDIT_EVENT_FAILED', 
        {...params, eventId : props.scheduledEvent!.eventId})
      }
    } else {
      setApiState({ loading : false, error : null, data : resp.data })
      
      if (props.scheduledEvent) {
        if (props.scheduledEvent.eventStatus === ScheduledEvent.EventStatus.ONGOING) {
          rc.events.broadcast(EVENTS.RELOAD_ONGOING_EVENTS)
          return
        }
      }
      rc.events.broadcast(EVENTS.RELOAD_SCHEDULED_EVENTS)
    }
  }

  const canEditEventType = props.mode === MODE.ADD || 
      (props.mode === MODE.EDIT && 
       props.scheduledEvent!.eventStatus === ScheduledEvent.EventStatus.SCHEDULED)

  const canEditDates = props.mode === MODE.ADD || 
      (props.mode === MODE.EDIT && 
      props.scheduledEvent!.eventStatus === ScheduledEvent.EventStatus.SCHEDULED)

      
  return (
    <BglDialog 
      disableEscapeKeyDown
      onClose={props.handleClose}
      aria-labelledby="customized-dialog-title"
      open={props.open}>
      
      <BglDialogTitle 
        title={props.mode === MODE.ADD ? 
          translations.getTranslation(EVENT_MANAGEMENT_STRINGS.SCHEDULE_POPUP.TITLE) : 
          translations.getTranslation(EVENT_MANAGEMENT_STRINGS.EDIT_POPUP.TITLE)}
        onClose={props.handleClose} />

      <form onSubmit={handleSubmit}>

        <DialogContent>

          <FormControl required fullWidth>
            <Typography variant="bglBody4" sx={{ marginBottom: '10px' }} color={theme.palette.text.secondary}>
              {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.EVENT_TYPE_TITLE)}
            </Typography>
            <BglSelect
              displayEmpty
              variant="outlined"
              id="event-type"
              value={eventType}
              disabled={!canEditEventType}
              input={<OutlinedInput/>}
              onChange={handleEventTypeChange}
              renderValue={(selected : any) => {
                if (!selected) {
                  return(
                    <Typography
                      component="span"
                      variant="bglBody2"
                      >
                        {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.INPUT_PLACEHOLDERS.SELECT_EVENT_TYPE)}
                    </Typography>
                  )
                }
                return selected
              }}>
              {
                supportedEvents.map((value : ScheduledEvent.EventType) => {
                  if (value === ScheduledEvent.EventType.ALL) return undefined
                  return (
                  <MenuItem
                    key={value}
                    value={value}>
                    <Typography variant="bglBody2">{value}</Typography>
                  </MenuItem>
                )})
              }
            </BglSelect>
          </FormControl>

          <div className="info-container" 
               style={{margin:"10px 0",padding:"20px",background:'lightgrey',display:'flex',flexDirection:'column',borderRadius: '10px'}}>
            <Typography variant="bglCaption1" sx={{fontWeight: 500}}  color={theme.palette.text.primary}>
              {rc.utils.replaceTokens(translations.getTranslation("EVENT_MANAGEMENT_STRINGS.POPUP.MESSAGE.PILOT_TIMEZONE_INFO"),{timezone: pilotTimeZone})}
            </Typography>
            <Typography variant="bglCaption2" sx={{ marginTop: '5px',fontWeight: 700}} color={theme.palette.text.primary}>
              {translations.getTranslation("EVENT_MANAGEMENT_STRINGS.POPUP.MESSAGE.PILOT_TIMEZONE_EMAIL")}
            </Typography>
          </div>

          <FormControl required sx={{ marginTop: '30px' }} fullWidth>
            <Typography variant="bglBody4"  
              sx={{ marginBottom: '10px'}} color={theme.palette.text.secondary} >
              {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.FIELD_LABEL.START_DATE)}
            </Typography>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              dateLibInstance={moment}>
              <BglDateTimePicker 
                inputFormat="MMM dd, yyyy hh:mm aa"
                disableMaskedInput
                label=""
                value={startDate}
                disabled={!canEditDates}
                minDateTime={rc.dateUtils.addMinutesToDate(new Date(),timeDiff)}
                onChange={onChangeStartDate}
                renderInput={(params) => {                  
                  params.inputProps!.placeholder = translations.getTranslation(EVENT_MANAGEMENT_STRINGS.INPUT_PLACEHOLDERS.DATE)
                  return (<TextField required {...params} />)
                }}/>
            </LocalizationProvider>
          </FormControl>

          <FormControl required sx={{ marginTop: '30px' }} fullWidth>
            <Typography variant="bglBody4"  
              sx={{ marginBottom: '10px'}} color={theme.palette.text.secondary}>
              {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.FIELD_LABEL.END_DATE)}
            </Typography>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}>
              <BglDateTimePicker 
                inputFormat="MMM dd, yyyy hh:mm aa"
                disableMaskedInput
                label=""
                value={endDate}
                disabled={!canEditDates}
                minDateTime={rc.dateUtils.addMinutesToDate(new Date(),timeDiff)}
                onChange={onChangeEndDate}
                renderInput={(params) => {                  
                  params.inputProps!.placeholder = translations.getTranslation(EVENT_MANAGEMENT_STRINGS.INPUT_PLACEHOLDERS.DATE)
                  return (<TextField required {...params} />)
                }}/>
            </LocalizationProvider>
            { endDateError && <FormHelperText error>{endDateError}</FormHelperText>}
          </FormControl>

          <Typography 
            component="div"
            variant="bglBody4" sx={{ marginTop: '20px'}} color={theme.palette.text.secondary}> 
            {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.FIELD_LABEL.DURATION)} : {duration} 
          </Typography>  

          {<Typography sx={{ marginTop: '5px', fontSize: "12px" }} color={theme.palette.text.secondary}> 
            { durDisclaimer } 
          </Typography> }     

          <FormControl required sx={{ marginTop: '30px' }} fullWidth>
            <Typography component="div"
              variant="bglBody4" sx={{ marginBottom: '10px'}} color={theme.palette.text.secondary}>
              {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.FIELD_LABEL.NAME)}
            </Typography>
            <TextField sx={{ background: 'white' }} 
              variant="outlined" 
              required
              label=''
              value={eventName}
              placeholder={translations.getTranslation(EVENT_MANAGEMENT_STRINGS.INPUT_PLACEHOLDERS.EVENT_NAME)}
              onChange={(event) => setEventName(event.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    { startDate && endDate && 
                      <IconButton
                        color="primary"
                        sx={{ marginRight: "-10px" }}
                        onClick={() => setEventNameFromDates(startDate, endDate, true)}
                        aria-label="next page"
                      >
                        {<RefreshIcon />}
                      </IconButton>
                    }
                  </InputAdornment>
                )
              }}/>
          </FormControl>

        </DialogContent>

        <BglDialogActions>
          
          <div className={styles.footerCont}>
                
          { // Loading view
            loading ? 
            <div className={styles.loadingCont}>
              <CircularProgress size={25} sx={{ marginRight: '10px' }}/>
              <Typography
              variant="bglBody2">{props.mode === MODE.EDIT 
                  ? translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.MESSAGE.SAVING) : 
                  translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.MESSAGE.SCHEDULING)}
              </Typography>
            </div> :
            <>
            { // Error view
              error ?
              <>
                <Typography variant="bglBody2" 
                sx={{ marginBottom: '20px'}} color="error">{error.message}</Typography>
                <div className={styles.btnCont}>
                  <BglCloseButton sx={{ minWidth: '180px' }}
                    onClick={()=>props.handleClose('CANCEL_BTN_CLICKED', 'FAILED_TO_SAVE')}>
                    {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.BTN.CANCEL)}
                  </BglCloseButton>

                  <BglPrimaryButton sx={{ minWidth: '180px' }} 
                    type="submit"> 
                    {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.BTN.TRY_AGAIN)}
                  </BglPrimaryButton>
                </div>
              </> : 

              // Success view
              data && data.eventId ?
              <>
                <div className={styles.loadingCont}>
                  <CheckCircleOutlineIcon color="success" sx={{ marginRight: '10px', marginBottom: '20px' }}/>
                  <Typography
                  variant="bglBody2" color="success">{props.mode === MODE.EDIT 
                      ? translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.MESSAGE.SAVED)
                      : translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.MESSAGE.SCHEDULED)}
                  </Typography>
                </div>
                <BglCloseButton sx={{ minWidth: '180px' }}
                  onClick={()=>props.handleClose('CANCEL_BTN_CLICKED', 'POST_SUCCESS')}>
                  {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.BTN.CLOSE)}
                </BglCloseButton>
              </> : 
              
              // Default view
              <div className={styles.btnCont}>
                <BglCloseButton sx={{ minWidth: '180px' }}
                  onClick={()=>{props.handleClose('CANCEL_BTN_CLICKED', 'NO_ACTION_TAKEN')}}>
                  {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.BTN.CANCEL)}
                </BglCloseButton>

                <BglPrimaryButton sx={{ minWidth: '180px' }}
                  type="submit"> 
                  { props.mode === MODE.EDIT ? 
                  translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.BTN.SAVE) : 
                  translations.getTranslation(EVENT_MANAGEMENT_STRINGS.POPUP.BTN.SCHEDULE) }
                </BglPrimaryButton>
              </div>
            }
            </>
          }
          
          </div>

        </BglDialogActions>

      </form>

    </BglDialog>

  )
}